"use client";

import { useSession } from "next-auth/react";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LogIn, Lock, Bot, Sparkles, Zap, PenTool } from "lucide-react";
import { AILoginModal } from "./ai-login-modal";

interface LoginRequiredProps {
  children: React.ReactNode;
  message?: string;
  feature?: "chat" | "homework" | "research" | "blog";
  redirect?: boolean;
}

export function LoginRequired({
  children,
  message,
  feature = "chat",
  redirect = false,
}: LoginRequiredProps) {
  const { data: session, status } = useSession();
  const [showModal, setShowModal] = useState(false);

  // Auto-open modal when component mounts and user is not authenticated
  useEffect(() => {
    if (status === "unauthenticated") {
      // Small delay to ensure smooth UI transition
      const timer = setTimeout(() => setShowModal(true), 100);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const getFeatureIcon = () => {
    switch (feature) {
      case "homework":
        return <Sparkles className="h-6 w-6 text-muted-foreground" />;
      case "research":
        return <Zap className="h-6 w-6 text-muted-foreground" />;
      case "blog":
        return <PenTool className="h-6 w-6 text-muted-foreground" />;
      default:
        return <Bot className="h-6 w-6 text-muted-foreground" />;
    }
  };

  const getFeatureTitle = () => {
    switch (feature) {
      case "homework":
        return "Homework Helper Access Required";
      case "research":
        return "Research Assistant Access Required";
      case "blog":
        return "Blog Creation Access Required";
      default:
        return "AI Chat Access Required";
    }
  };

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (status === "unauthenticated" || !session) {
    return (
      <>
        <div className="container mx-auto py-10 px-4 md:px-6">
          <div className="max-w-md mx-auto">
            <Card className="border-2 border-dashed">
              <CardHeader className="text-center">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  {getFeatureIcon()}
                </div>
                <CardTitle>{getFeatureTitle()}</CardTitle>
                <CardDescription>
                  {message ||
                    "Please sign in to access our AI-powered learning tools"}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center text-sm text-muted-foreground">
                <p>
                  Our AI-powered learning tools require you to be signed in to:
                </p>
                <ul className="mt-2 space-y-1 text-left max-w-xs mx-auto">
                  <li>• Save your learning progress</li>
                  <li>• Access personalized features</li>
                  <li>• Maintain conversation history</li>
                  <li>• Get customized assistance</li>
                </ul>
              </CardContent>
              <CardFooter className="flex flex-col gap-3">
                <Button onClick={() => setShowModal(true)} className="w-full">
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign In with Google
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Quick and secure sign-in powered by Google OAuth
                </p>
              </CardFooter>
            </Card>
          </div>
        </div>

        <AILoginModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onLoginSuccess={() => {
            setShowModal(false);
            // The page will automatically update due to session change
          }}
          feature={feature}
          redirectUrl={
            typeof window !== "undefined" ? window.location.href : undefined
          }
        />
      </>
    );
  }

  return <>{children}</>;
}

export default LoginRequired;
